import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import cityComparisonService from "../../api/cityComparisonService";

export const share = createAsyncThunk(
  "/cityComparison/share",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await cityComparisonService.share(payload);

      return {
        shareSuccess: response?.data || [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
export const sharedComparison = createAsyncThunk(
  "/cityComparison/sharedComparison",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await cityComparisonService.sharedComparison(payload);

      return {
        sharedComparisonSuccess: response?.data || [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const sharedComparisonPDF = createAsyncThunk(
  "/cityComparison/sharedComparisonPdf",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await cityComparisonService.sharedComparisonPDF(
        payload,
        { responseType: "blob" },
      );

      // 🔥 Trigger download inside thunk
      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "City_Comparison.pdf";
      link.click();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const ComparisonTrack = createAsyncThunk(
  "/cityComparison/ComparisonTrack",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await cityComparisonService.ComparisonTrack(payload);

      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
export const sessionId = createAsyncThunk(
  "/cityComparison/sessionId",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await cityComparisonService.sessionId(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
export const exportAnalytics = createAsyncThunk(
  "/cityComparison/exportAnalytics",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await cityComparisonService.exportAnalytics(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
export const popularWorkLocations = createAsyncThunk(
  "/cityComparison/popularWorkLocations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await cityComparisonService.popularWorkLocations();
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Initial state
const initialState = {
  shareItems: {},
  sharedComparisonItems: {},
  sharedComparisonPDFItems: {},
  ComparisonTrackItems: {},
  sessionIdItems: {},
  exportAnalyticsItems: {},
  popularWorkLocationsItems: {},
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: "",
  successMessage: "",
};

const cityComparisonSlice = createSlice({
  name: "career",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(share.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(share.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.shareItems = action.payload.shareSuccess || "";
        state.errorMessage = "";
      })
      .addCase(share.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(sharedComparison.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(sharedComparison.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.sharedComparisonItems =
          action.payload.sharedComparisonSuccess || "";
        state.errorMessage = "";
      })
      .addCase(sharedComparison.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(ComparisonTrack.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(ComparisonTrack.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.ComparisonTrackItems = action.payload || "";
      })
      .addCase(ComparisonTrack.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(sessionId.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(sessionId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.sessionIdItems = action.payload.sessionIdSuccess || "";
        state.errorMessage = "";
      })
      .addCase(sessionId.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(exportAnalytics.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(exportAnalytics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.exportAnalyticsItems = action.payload || "";
        state.errorMessage = "";
      })
      .addCase(exportAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(popularWorkLocations.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(popularWorkLocations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.popularWorkLocationsItems = action.payload || "";
        state.errorMessage = "";
      })
      .addCase(popularWorkLocations.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })
      .addCase(sharedComparisonPDF.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isSuccess = false;
        state.errorMessage = "";
      })
      .addCase(sharedComparisonPDF.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.sharedComparisonPDFItems =
          action.payload.sharedComparisonPDFSuccess || "";
        state.errorMessage = "";
      })
      .addCase(sharedComparisonPDF.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      });
  },
});

export default cityComparisonSlice.reducer;
